import { useState, useEffect } from 'react';
import { Stack } from '@mui/material';
import Chips from '../chips.component';
import RegulationItem, { ResultType } from './regulation-item.component';

const RegulationList = ({ regulationData, onChipDelete, chipLabel, regulationSubject, subjectListSelect }) => {
    // Variables for utility functions
    const [subjectList, setSubjectList] = useState([]);
    const [subject, setSubject] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [sortValue, setSortValue] = useState('region');
    const [resultTypeSelected, setResultTypeSelected] = useState('all');
    const [currentPageData, setCurrentPageData] = useState();

    useEffect(() => {
        setSubject(regulationSubject);
    }, [regulationSubject]);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            await mapRegulation();
            setIsLoading(false);
        })();
    }, [regulationData, resultTypeSelected, subject, sortValue]);

    useEffect(() => {
        (async () => {
            await getRegulationSubjects();
        })();
    }, [regulationData, resultTypeSelected]);

    // Retrieve the list of subjects based on the selected result type
    const getRegulationSubjects = async () => {
        //filter subjects based on selected result type "All, AltFuel, Region, Country, Incentive, MOU, Port, State"
        if (resultTypeSelected.toLowerCase().includes(ResultType.All.toLowerCase()) && subjectListSelect) {
            setSubjectList(() =>
                subjectListSelect.All.map((item) => {
                    return { name: item };
                })
            );
        }
        if (resultTypeSelected.toLowerCase().includes(ResultType.AltFuel.toLowerCase()) && subjectListSelect) {
            setSubjectList(() =>
                subjectListSelect.FuelCountry.map((item) => {
                    return { name: item };
                })
            );
        }
        if (resultTypeSelected.toLowerCase().includes(ResultType.Region.toLowerCase()) && subjectListSelect) {
            setSubjectList(() =>
                subjectListSelect.Region.map((item) => {
                    return { name: item };
                })
            );
        }
        if (resultTypeSelected.toLowerCase().includes(ResultType.Country.toLowerCase()) && subjectListSelect) {
            setSubjectList(() =>
                subjectListSelect.Country.map((item) => {
                    return { name: item };
                })
            );
        }
        if (resultTypeSelected.toLowerCase().includes(ResultType.Incentive.toLowerCase()) && subjectListSelect) {
            setSubjectList(() =>
                subjectListSelect.PortIncentive.map((item) => {
                    return { name: item };
                })
            );
        }
        if (resultTypeSelected.toLowerCase().includes(ResultType.Port.toLowerCase()) && subjectListSelect) {
            setSubjectList(() =>
                subjectListSelect.Port.map((item) => {
                    return { name: item };
                })
            );
        }
        if (resultTypeSelected.toLowerCase().includes(ResultType.State.toLowerCase()) && subjectListSelect) {
            setSubjectList(() =>
                subjectListSelect.State.map((item) => {
                    return { name: item };
                })
            );
        }
    };

    const mapRegulation = async () => {
        let regulations = [];

        // filter regulations based on selected result type
        if (resultTypeSelected === ResultType.All) {
            // if sortValue filter is active then 'sort' by selected sortValue (this can be improved)

            if (sortValue) {
                switch (sortValue) {
                    case 'port':
                        regulations = regulationData.Port.concat(regulationData.State)
                            .concat(regulationData.Country)
                            .concat(regulationData.Region)
                            .concat(regulationData.PortIncentive)
                            .concat(regulationData.FuelCountry);

                        break;
                    case 'state':
                        regulations = regulationData.State.concat(regulationData.Port)
                            .concat(regulationData.Country)
                            .concat(regulationData.Region)
                            .concat(regulationData.PortIncentive)
                            .concat(regulationData.FuelCountry);

                        break;
                    case 'region':
                        regulations = regulationData.Region.concat(regulationData.Country)
                            .concat(regulationData.State)
                            .concat(regulationData.Port)
                            .concat(regulationData.PortIncentive)
                            .concat(regulationData.FuelCountry);
                        break;
                }
            } else {
                regulations = regulationData.All;
            }
        } else if (resultTypeSelected === ResultType.Region) {
            regulations = regulationData.Region;
        } else if (resultTypeSelected === ResultType.Country) {
            regulations = regulationData.Country;
        } else if (resultTypeSelected === ResultType.State) {
            regulations = regulationData.State;
        } else if (resultTypeSelected === ResultType.Port) {
            regulations = regulationData.Port;
        } else if (resultTypeSelected === ResultType.Incentive) {
            regulations = regulationData.PortIncentive;
        } else if (resultTypeSelected === ResultType.AltFuel) {
            regulations = regulationData.FuelCountry;
        }

        // if subject filter is active then filter regulations further by subject
        if (subject) {
            regulations = regulations.filter((f) => subject.name.includes(f.Subject));
        }

        // map to new regulations object to comply with 'RegulationSection' component expected regulations schema.
        const regulationObject = regulations.map((item) => {
            const newItem = {
                region: item.Region,
                country: item.Country,
                state: item.State,
                port: item.Port,
                campaign: item.Campaign,
                regulator: item.Regulator,
                name: item.Name,
                description: item.Description,
                link_url: item.Link_Url,
                link_display: item.Link_Display,
                subject: item.Subject,
                special_area: item.SpecialArea,
                start_date: item.StartDate,
                end_date: item.EndDate
            };

            return newItem;
        });

        setCurrentPageData(regulationObject);
    };

    const handleResultTypeChange = (event) => {
        setResultTypeSelected(event.target.value);
    };

    const handleSubjectChange = (event, data) => {
        setSubject(data);
    };

    const handleSortChange = (event) => {
        setSortValue(event.target.value);
    };

    const handleChipDelete = () => {
        onChipDelete();
    };

    const getChipLabel = () => {
        return [
            {
                name: chipLabel
            }
        ];
    };

    return (
        <>
            {!isLoading && currentPageData && subjectList && (
                <Stack direction={'column'} spacing={1}>
                    <Chips sx={{ width: { xs: '200px', sm: '200px', md: '200px' } }} handleChipDelete={handleChipDelete} filters={getChipLabel()} />
                    <RegulationItem
                        regulationData={currentPageData}
                        resultTypeSelected={resultTypeSelected}
                        onResultTypeChange={handleResultTypeChange}
                        sortTypeSelected={sortValue}
                        onSortTypeChange={handleSortChange}
                        subjectList={subjectList}
                        subjectSelected={subject}
                        onSubjectChange={handleSubjectChange}
                    />
                </Stack>
            )}
        </>
    );
};

export default RegulationList;
